/**
 * Paseo Road PDP Complete the Set (tracker #217).
 *
 * The Liquid snippet owns merchandising rules and product data. This custom
 * element owns only customer interaction: matching insert sizes to the active
 * PDP variant, preserving explicit overrides, and assembling one multi-line
 * cart request. It deliberately dispatches the same `cart:item-added` event as
 * Paseo's Alpine product form so the native cart drawer remains the only cart
 * UI path.
 */

class PaseoCompleteSet extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.productRoot = this.closest('.core-product');
    this.cards = [...this.querySelectorAll('[data-complete-set-card]')];
    this.status = this.querySelector('[data-complete-set-status]');
    this.error = this.querySelector('[data-complete-set-error]');
    this.mainVariants = this.readMainVariants();
    this.syncQueued = false;

    this.cards.forEach((card) => this.initializeCard(card));

    this.handleRootInteraction = (event) => {
      if (this.contains(event.target)) return;
      if (!event.target.closest('.core-product-details__options, .pr-sticky-root')) return;
      this.queueSync();
    };

    this.productRoot?.addEventListener('click', this.handleRootInteraction);
    this.productRoot?.addEventListener('change', this.handleRootInteraction);
    window.addEventListener('popstate', this.queueSyncBound = () => this.queueSync());

    this.syncFromMainVariant();
    this.refreshStatus();
  }

  disconnectedCallback() {
    this.productRoot?.removeEventListener('click', this.handleRootInteraction);
    this.productRoot?.removeEventListener('change', this.handleRootInteraction);
    if (this.queueSyncBound) window.removeEventListener('popstate', this.queueSyncBound);
  }

  readMainVariants() {
    const config = this.productRoot?.querySelector('[data-product-form-config]');
    if (!config) return [];
    try {
      const parsed = JSON.parse(config.textContent);
      return Array.isArray(parsed.variants) ? parsed.variants : [];
    } catch (error) {
      console.error('Paseo Complete the Set: invalid product configuration.', error);
      return [];
    }
  }

  initializeCard(card) {
    const checkbox = card.querySelector('[data-complete-set-check]');
    const select = card.querySelector('[data-complete-set-variant]');
    const quantity = card.querySelector('[data-complete-set-quantity]');
    const decrement = card.querySelector('[data-complete-set-decrement]');
    const increment = card.querySelector('[data-complete-set-increment]');

    checkbox?.addEventListener('change', () => {
      card.toggleAttribute('data-selected', checkbox.checked);
      this.refreshStatus();
    });

    select?.addEventListener('change', () => {
      select.dataset.userChose = 'true';
      this.updateCardPrice(card);
    });

    if (quantity) {
      const clamp = () => {
        const value = Math.min(6, Math.max(1, Number.parseInt(quantity.value, 10) || 1));
        quantity.value = String(value);
        if (decrement) decrement.disabled = value <= 1;
        if (increment) increment.disabled = value >= 6;
      };
      const changeBy = (delta) => {
        quantity.value = String((Number.parseInt(quantity.value, 10) || 1) + delta);
        quantity.dataset.userChose = 'true';
        clamp();
      };
      decrement?.addEventListener('click', () => changeBy(-1));
      increment?.addEventListener('click', () => changeBy(1));
      quantity.addEventListener('input', () => {
        quantity.dataset.userChose = 'true';
        clamp();
      });
      quantity.addEventListener('change', clamp);
      clamp();
    }
  }

  queueSync() {
    if (this.syncQueued) return;
    this.syncQueued = true;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.syncQueued = false;
      this.syncFromMainVariant();
    }));
  }

  getMainVariant() {
    const input = this.productRoot?.querySelector('.core-product-details__form [name="id"]');
    const inputId = Number.parseInt(input?.value, 10);
    if (inputId) return this.mainVariants.find((variant) => variant.id === inputId) || null;

    try {
      const urlId = Number.parseInt(new URL(window.location.href).searchParams.get('variant'), 10);
      if (urlId) return this.mainVariants.find((variant) => variant.id === urlId) || null;
    } catch (_) {}

    return this.mainVariants[0] || null;
  }

  extractSize(value) {
    if (!value) return null;
    const normalized = String(value).toLowerCase().replace(/\s+/g, ' ').trim();
    const tokens = [
      'king/cal king', 'king / cal king',
      'twin/twin xl', 'twin / twin xl',
      'cal king', 'california king',
      'super king', 'super queen',
      'full/queen', 'full / queen',
      'queen', 'king', 'twin xl', 'twin', 'full', 'standard',
    ];
    return tokens.find((token) => normalized.includes(token)) || null;
  }

  isPillowInsert(card) {
    const handle = card.dataset.handle || '';
    return handle.includes('pillow-sham') || handle.includes('sham-insert');
  }

  candidateSizes(mainSize, card) {
    if (this.isPillowInsert(card)) {
      const standard = ['twin', 'twin xl', 'twin/twin xl', 'twin / twin xl', 'full', 'full/queen', 'full / queen', 'queen', 'super queen'];
      return standard.includes(mainSize) ? ['standard'] : ['king'];
    }

    const equivalents = {
      'twin': ['twin'],
      'twin xl': ['twin xl', 'twin'],
      'twin/twin xl': ['twin/twin xl', 'twin / twin xl', 'twin xl', 'twin'],
      'twin / twin xl': ['twin/twin xl', 'twin / twin xl', 'twin xl', 'twin'],
      'full': ['full', 'full/queen', 'full / queen'],
      'full/queen': ['full/queen', 'full / queen', 'queen', 'full'],
      'full / queen': ['full/queen', 'full / queen', 'queen', 'full'],
      'queen': ['queen', 'full/queen', 'full / queen'],
      'super queen': ['queen', 'full/queen', 'full / queen'],
      'king': ['king', 'king/cal king', 'king / cal king'],
      'cal king': ['king', 'cal king', 'california king', 'king/cal king', 'king / cal king'],
      'california king': ['king', 'cal king', 'california king', 'king/cal king', 'king / cal king'],
      'super king': ['king', 'king/cal king', 'king / cal king'],
      'king/cal king': ['king', 'king/cal king', 'king / cal king', 'cal king', 'california king'],
      'king / cal king': ['king', 'king/cal king', 'king / cal king', 'cal king', 'california king'],
    };
    return equivalents[mainSize] || [mainSize];
  }

  matchingOption(card, mainSize) {
    const select = card.querySelector('select[data-complete-set-variant]');
    if (!select || !mainSize) return null;
    const candidates = this.candidateSizes(mainSize, card);

    for (const size of candidates) {
      const matches = [...select.options].filter((option) => {
        if (option.disabled) return false;
        return this.extractSize(option.dataset.title || option.textContent) === size;
      });
      if (matches.length === 0) continue;
      const selected = matches.find((option) => option.value === select.value);
      return selected || matches[0];
    }
    return null;
  }

  syncFromMainVariant() {
    const mainVariant = this.getMainVariant();
    if (!mainVariant) return;
    const mainTitle = String(mainVariant.title || '').toLowerCase();
    const mainSize = this.extractSize(mainVariant.title)
      || this.extractSize(mainVariant.option1)
      || this.extractSize(mainVariant.option2)
      || this.extractSize(mainVariant.option3);

    this.cards.forEach((card) => {
      const mode = card.dataset.variantMode || 'always';
      const visible = mode === 'always'
        || (mode === 'duvet' && mainTitle.includes('duvet'))
        || (mode === 'comforter' && mainTitle.includes('comforter'));
      card.hidden = !visible;
      if (!visible) {
        // DEV-452: the snippet's `display: grid` outranks the `hidden`
        // attribute, so the duvet insert kept showing for Comforter Set and
        // Blanket Set options. Hide it the way HEA does (inline display), so
        // duvet inserts appear only with a Duvet Cover Set.
        this.uncheck(card);
        card.style.display = 'none';
        return;
      }

      this.syncQuantity(card, mainSize);
      const select = card.querySelector('select[data-complete-set-variant]');
      if (!select) {
        this.setCardAvailability(card, true);
        return;
      }

      const match = this.matchingOption(card, mainSize);
      if (!match) {
        this.setCardAvailability(card, false);
        return;
      }

      this.setCardAvailability(card, true);
      if (select.dataset.userChose !== 'true' && select.value !== match.value) {
        select.value = match.value;
      }
      this.updateCardPrice(card);
    });

    this.refreshBlockVisibility();
    this.refreshStatus();
  }

  // DEV-452 (port of HEA DEV-446): hide the whole block, heading included,
  // when no card is left for this size; show it again when one returns.
  // Uses each card's own computed display, so a card hidden for any reason
  // counts as gone.
  refreshBlockVisibility() {
    const anyVisible = this.cards.some((card) => !card.hasAttribute('data-size-hidden')
      && getComputedStyle(card).display !== 'none');
    this.style.display = anyVisible ? '' : 'none';
  }

  syncQuantity(card, mainSize) {
    if (!this.isPillowInsert(card) || !mainSize) return;
    const quantity = card.querySelector('[data-complete-set-quantity]');
    if (!quantity || quantity.dataset.userChose === 'true') return;
    const singleShamSizes = ['twin', 'twin xl', 'twin/twin xl', 'twin / twin xl'];
    quantity.value = singleShamSizes.includes(mainSize) ? '1' : '2';
    quantity.dispatchEvent(new Event('change'));
  }

  setCardAvailability(card, available) {
    const checkbox = card.querySelector('[data-complete-set-check]');
    const notice = card.querySelector('[data-complete-set-unavailable]');
    if (checkbox) checkbox.disabled = !available;
    card.toggleAttribute('data-unavailable', !available);
    if (notice) notice.hidden = available;
    if (!available) this.uncheck(card);
    // DEV-452: an insert with nothing in stock in the shopper's size is
    // hidden (no greyed "Not available in your size." card above Add to
    // Cart); it comes back when the size changes to one it has.
    card.toggleAttribute('data-size-hidden', !available);
    card.style.display = available ? '' : 'none';
  }

  updateCardPrice(card) {
    const select = card.querySelector('select[data-complete-set-variant]');
    const option = select?.options[select.selectedIndex];
    const price = card.querySelector('[data-complete-set-price]');
    const compare = card.querySelector('[data-complete-set-compare]');
    if (price && option?.dataset.price) price.textContent = option.dataset.price;
    if (compare) {
      compare.textContent = option?.dataset.compare || '';
      compare.hidden = !option?.dataset.compare;
    }
  }

  uncheck(card) {
    const checkbox = card.querySelector('[data-complete-set-check]');
    if (checkbox) checkbox.checked = false;
    card.removeAttribute('data-selected');
  }

  selectedItems() {
    return this.cards.flatMap((card) => {
      const checkbox = card.querySelector('[data-complete-set-check]');
      if (card.hidden || card.hasAttribute('data-size-hidden') || checkbox?.disabled || !checkbox?.checked) return [];
      const variant = card.querySelector('[data-complete-set-variant]');
      const id = Number.parseInt(variant?.value, 10);
      const quantityInput = card.querySelector('[data-complete-set-quantity]');
      const quantity = Math.min(6, Math.max(1, Number.parseInt(quantityInput?.value, 10) || 1));
      return id ? [{ id, quantity }] : [];
    });
  }

  hasSelectedItems() {
    return this.selectedItems().length > 0;
  }

  refreshStatus() {
    if (!this.status) return;
    const items = this.selectedItems();
    const units = items.reduce((sum, item) => sum + item.quantity, 0);
    this.status.textContent = units > 0
      ? `${units} ${units === 1 ? 'insert' : 'inserts'} will be added with this item.`
      : '';
    this.status.hidden = units === 0;
  }

  async addWithMain(mainVariant, quantity = 1) {
    if (!mainVariant?.id) throw new Error('Choose an available product option before adding to cart.');
    const items = [
      { id: Number(mainVariant.id), quantity: Math.max(1, Number.parseInt(quantity, 10) || 1) },
      ...this.selectedItems(),
    ];

    if (this.error) this.error.textContent = '';

    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = payload.description || payload.message || 'We could not add every item. Please try again.';
      if (this.error) this.error.textContent = message;
      throw new Error(message);
    }

    this.cards.forEach((card) => this.uncheck(card));
    this.refreshStatus();
    this.dispatchEvent(new CustomEvent('cart:item-added', {
      bubbles: true,
      detail: { item: payload, cart: payload },
    }));
    return payload;
  }
}

if (!customElements.get('paseo-complete-set')) {
  customElements.define('paseo-complete-set', PaseoCompleteSet);
}
